import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { Plus, Search, Filter, Trash2 } from "lucide-react";
import { useContracts, useDeleteContract } from "../hooks";
import {
  Card,
  Button,
  Badge,
  LoadingSpinner,
  Alert,
} from "../components/common";
import Modal from "../components/common/Modal";
import type { ContractStatus } from "../types";

const statusOptions: { value: string; label: string }[] = [
  { value: "", label: "All Status" },
  { value: "draft", label: "Draft" },
  { value: "generated", label: "Generated" },
];

export default function ContractList() {
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState("");
  // Initialise from ?search= so palette overflow rows ("Show all N…") pre-populate the field.
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteContract();

  const closeDeleteModal = () => {
    if (deleteMutation.isPending) return;
    setDeletingId(null);
    setDeleteError(null);
  };

  const { data, isLoading, error } = useContracts({
    status: statusFilter || undefined,
    limit: 50,
  });

  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return data?.items.filter(
      (contract) =>
        contract.title.toLowerCase().includes(query) ||
        contract.referenceNumber?.toLowerCase().includes(query),
    );
  }, [data?.items, searchQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">Contracts</h1>
        <Link to="/generate/contracts/new">
          <Button icon={<Plus className="h-4 w-4" />}>New Contract</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search contracts"
              className="input pl-10"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              className="input pl-10 pr-8 appearance-none cursor-pointer"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Contract List */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Failed to load contracts</p>
          </div>
        ) : !filteredItems?.length ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No contracts found</p>
            {(statusFilter || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  setStatusFilter("");
                  setSearchQuery("");
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    Reference
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    Parties
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    Updated
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-overline uppercase text-slate-500 dark:text-slate-400 tracking-wider"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredItems.map((contract) => (
                  <tr
                    key={contract.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={
                          contract.status === "draft" &&
                          contract.wizardStep !== "complete"
                            ? `/generate/contracts/new?resume=${contract.id}`
                            : `/generate/contracts/${contract.id}`
                        }
                        className="font-medium text-slate-900 dark:text-slate-50 hover:text-brand-600 dark:hover:text-brand-400"
                      >
                        {contract.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                      {contract.referenceNumber || "\u2014"}
                    </td>
                    <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                      {(contract.parties || []).map((p) => p.name).join(", ") ||
                        "No parties"}
                    </td>
                    <td className="px-6 py-4">
                      <Badge status={contract.status as ContractStatus} />
                    </td>
                    <td className="px-6 py-4 text-body-sm text-slate-500 dark:text-slate-400">
                      {new Date(contract.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {contract.status === "draft" && (
                        <Button
                          variant="danger"
                          iconOnly
                          size="sm"
                          icon={<Trash2 className="h-4 w-4" />}
                          aria-label={`Delete ${contract.title}`}
                          className="btn-icon-compact"
                          onClick={() => setDeletingId(contract.id)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={!!deletingId}
        onClose={closeDeleteModal}
        title="Delete Draft"
      >
        <p className="text-body text-slate-600 dark:text-slate-400 mb-6">
          Are you sure you want to delete this draft? This action cannot be
          undone.
        </p>
        {deleteError && (
          <Alert variant="error" className="mb-4">
            {deleteError}
          </Alert>
        )}
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={closeDeleteModal}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (!deletingId) return;
              try {
                setDeleteError(null);
                await deleteMutation.mutateAsync(deletingId);
                setDeletingId(null);
              } catch {
                setDeleteError("Failed to delete draft. Please try again.");
              }
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
